import { Forms, getDisplayCategoryForm } from "~/utils/products";
import { convertBankBalance } from "../bankCalculations/balance";
import { convertPensionBalance } from "../pensionCalculations/balance";
import { convertShareBalance } from "../shareCalculations/balance";
import { convertRSUBalance } from "../shareCalculations/rsu";
import { convertCryptoBalance } from "../cryptoCalculations/balance";
import { debtBalance } from "../debtCalculations/balance";
import { calculateRealestateNetBalanceBase } from "../realestateCalculations/balance";
import { convertOtherBalance } from "../otherCalculations/balance";
import type {
  Fiat,
  FrontendCryptoFormat,
  FrontendStockFormat,
} from "~/types/global.types";
import type { CryptoCoin, Stock } from "~/types/assets.types";
import { bankInterest } from "../bankCalculations/interest";
import { calculatePensionYearlyYield } from "../pensionCalculations/yearlyYield";
import { calculateRSUsCurrentYield, calculateStocksCurrentYield } from "../shareCalculations/currentYield";
import { calculateCryptoCurrentYield } from "../cryptoCalculations/currentYield";
import { calculateDebtWeightedInterest } from "../debtCalculations/weightedInterest";
import { calculateRealestateCurrentYield } from "../realestateCalculations/currentYield";
import { otherCurrentYield } from "../otherCalculations/currentYield";
import { convertCurrency } from "../genericCalculations/convertCurrency";
import { fallbackFiat } from "~/utils/fallbacks";
import { findFormattedStock } from "~/utils/taseSecurityId";
import { resolveShareStockPrice } from "../shareCalculations/resolveStockPrice";

export const calculateBalances = (
  products: Record<string, any>[],
  fiat: Fiat[],
  crypto: FrontendCryptoFormat[],
  stocks: FrontendStockFormat[],
  flags?: {
    currency?: boolean;
    liquid?: boolean;
    bankBalance?: boolean;
    form?: boolean;
    allForms?: boolean;
    autoImported?: boolean;
  }
) => {
  const baseCurrency = fiat.find((currency) => currency.value === "ILS");
  const stockCryptoCurrency = fiat.find((currency) => currency.value === "USD");

  const balancePerProduct: Record<string, Function> = {
    [Forms.BANK]: convertBankBalance,
    [Forms.PENSION]: convertPensionBalance,
    [Forms.SHARE]: convertShareBalance,
    [Forms.RSU]: convertRSUBalance,
    [Forms.CRYPTO]: convertCryptoBalance,
    [Forms.DEBT]: debtBalance,
    [Forms.REALESTATE]: calculateRealestateNetBalanceBase,
    [Forms.OTHER]: convertOtherBalance,
  };

  const interestPerProduct: Record<string, Function> = {
    [Forms.BANK]: bankInterest,
    [Forms.PENSION]: calculatePensionYearlyYield,
    [Forms.SHARE]: calculateStocksCurrentYield,
    [Forms.RSU]: calculateRSUsCurrentYield,
    [Forms.CRYPTO]: calculateCryptoCurrentYield,
    [Forms.DEBT]: calculateDebtWeightedInterest,
    [Forms.REALESTATE]: calculateRealestateCurrentYield,
    [Forms.OTHER]: otherCurrentYield,
  };

  const balancesInBase: Record<number, number> = {};
  const interests: Record<number, { form: Forms; interest: number }> = {};

  const totalBalances = {
    assets: 0,
    debts: 0,
    ...(flags?.autoImported && { autoImported: 0 }),
    ...(flags?.currency && { foreignAssets: 0 }),
    ...(flags?.liquid && { liquidAssets: 0 }),
    ...(flags?.liquid && { nonLiquidAssets: 0 }),
    ...(flags?.bankBalance && { balanceAssets: 0 }),
    ...(flags?.form && { shareAssets: 0,rsuAssets: 0, cryptoAssets: 0, otherAssets: 0 }),
    ...(flags?.allForms && {
      bankAssets: 0,
      pensionAssets: 0,
      shareAssets: 0,
      rsuAssets: 0,
      cryptoAssets: 0,
      debtAssets: 0,
      realestateAssets: 0,
      otherAssets: 0,
      autoImported: 0,
      lastUpdate: {
        rsuAssets: undefined,
        bankAssets: undefined,
        pensionAssets: undefined,
        shareAssets: undefined,
        cryptoAssets: undefined,
        debtAssets: undefined,
        realestateAssets: undefined,
        otherAssets: undefined,
      } as Record<string, string | undefined>,
    }),
    ...(flags?.bankBalance && { balanceAssetNames: [] as string[] }),
    all() {
      return (
        this.pensionAssets! +
        this.debtAssets! +
        this.realestateAssets! +
        this.otherAssets! +
        this.bankAssets! +
        this.shareAssets! +
        this.rsuAssets! +
        this.cryptoAssets!
      );
    },
  };

  const getCurrency = (product: Record<string, any>) => {
    const currCurrency = product?.currency;
    const currentCurrency = fiat.find(
      (currency) => currency.value === currCurrency?.value
    );

    return { currentCurrency };
  };

  const setupCryptoProduct = (product: Record<string, any>) => {
    const currentCurrency = stockCryptoCurrency;
    const updatedProduct = {
      ...product,
    };

    const cashCurrency = fiat.find(
      (currency) => currency.value === product?.currency?.value
    );

    updatedProduct.cryptoData = product?.cryptoData?.map((coin: CryptoCoin) => {
      const desiredCoin = crypto.find(
        (formattedCoin) => formattedCoin.value === coin.coinName
      );
      const coinData = {
        ...coin,
        coinName: desiredCoin?.name || coin.coinName,
        coinRate: desiredCoin?.rate ?? coin.coinRate,
      };
      return coinData;
    });

    return { updatedProduct, currentCurrency, cashCurrency };
  };


  const setupShareProduct = (product: Record<string, any>) => {
    const currentCurrency = stockCryptoCurrency;
    const updatedProduct = {
      ...product,
    };
    const baseCurrency = fiat.find((currency) => currency.value === "ILS") || fallbackFiat;
    const cashCurrency = fiat.find(
      (currency) => currency.value === product?.currency?.value
    );

    updatedProduct.stocksData = product?.stocksData?.map((stock: Stock) => {
      const desiredStock = findFormattedStock(stocks, stock.stockName, stock.purchasePrice);
      // stock.currency might already be a Fiat or a string code depending on save path
      const stockCurrency = ((): Fiat => {
        if ((stock as any)?.currency && typeof (stock as any)?.currency === 'object' && (stock as any)?.currency?.value) {
          return (stock as any).currency as unknown as Fiat;
        }
        const code = (stock as any)?.currency as unknown as string;
        return fiat.find((currency) => currency.value === code) || fallbackFiat;
      })();
      // Convert purchasePrice if currencies differ
      const finalCurrency = desiredStock?.currency || stockCurrency;
      const getConvertedPurchasePrice = (): number => {
        const originalPurchasePrice = stock.purchasePrice || 0;
        if (!desiredStock?.currency || desiredStock.currency.value === stockCurrency.value) {
          return originalPurchasePrice;
        }
        // Convert from stock currency to desiredStock currency
        return convertCurrency(originalPurchasePrice, desiredStock.currency, stockCurrency);
      };

      const productPrice = stock.stockPrice || 0;
      const productPriceInDesiredCurrency =
        productPrice &&
        desiredStock?.currency &&
        desiredStock.currency.value !== stockCurrency.value
          ? convertCurrency(productPrice, desiredStock.currency, stockCurrency)
          : productPrice;
      const convertedPurchasePrice = getConvertedPurchasePrice();
      const finalPrice = resolveShareStockPrice(
        desiredStock?.worth,
        productPriceInDesiredCurrency,
        convertedPurchasePrice
      );
      
      const stockData = {
        ...stock,
        stockName: desiredStock?.name || stock.stockName,
        stockPrice: finalPrice,
        currency: finalCurrency,
        purchasePrice: convertedPurchasePrice,
        stockPriceInBaseCurrency: convertCurrency(finalPrice, baseCurrency, finalCurrency),
      };
      return stockData;
    });

    return { updatedProduct, currentCurrency, cashCurrency };
  };

  const setupRSUProduct = (product: Record<string, any>) => {

    const currentCurrency = stockCryptoCurrency;
    let updatedProduct = {
      ...product,
    };

    const productCurrency = fiat.find(
      (currency) => currency.value === product?.currency?.value
    ) || fallbackFiat;
    const baseCurrency = fiat.find((currency) => currency.value === "ILS") || fallbackFiat;
    let matchingStock = stocks.find(
      (formattedStock) => formattedStock.value === product.stockName || formattedStock.name === product.stockName
    );
    if (!matchingStock) {
      updatedProduct = {
        ...updatedProduct,
        stockPrice: updatedProduct.stockPrice || 0,
        stockName: updatedProduct.stockName || "",
        currency: productCurrency,
        stockPriceInBaseCurrency: convertCurrency(updatedProduct.stockPrice || 0, baseCurrency, productCurrency),
      }
    }
    else{
      updatedProduct = {
        ...updatedProduct,
        stockPrice: matchingStock.worth,
        currency: matchingStock.currency,
        stockPriceInBaseCurrency: matchingStock.worthInBaseCurrency,
    }
  }

    return { updatedProduct, currentCurrency, cashCurrency: productCurrency };
  }

  const setupDictionary: Record<string, Function> = {
    [Forms.BANK]: getCurrency,
    [Forms.PENSION]: getCurrency,
    [Forms.SHARE]: setupShareProduct,
    [Forms.RSU]: setupRSUProduct,
    [Forms.CRYPTO]: setupCryptoProduct,
    [Forms.DEBT]: getCurrency,
    [Forms.REALESTATE]: getCurrency,
    [Forms.OTHER]: getCurrency,
  };

  products?.forEach((product, index) => {
    const { id, form } = product;
    const setupCallback = setupDictionary[form];
    const { updatedProduct, currentCurrency, cashCurrency } =
      setupCallback?.(product);
    if (updatedProduct) products[index] = updatedProduct;

    const callback = balancePerProduct[form];
    const interestCallback = interestPerProduct[form];

    const balance =
      callback?.(
        updatedProduct || product,
        {
          baseCurrency,
          cashCurrency,
          currentCurrency,
          stockCurrency: currentCurrency,
          cryptoCurrency: currentCurrency,
        },
        true
      ) || 0;
    const interest =
      interestCallback?.(
        updatedProduct || product,
        {
          baseCurrency,
          cashCurrency,
          currentCurrency,
          stockCurrency: currentCurrency,
          cryptoCurrency: currentCurrency,
        },
        true
      ) || 0;

    balancesInBase[id] = balance;
    if (product.isFamilyJointDuplicate) {
      return;
    }
    interests[id] = {
      form,
      interest,
    };
    totalBalances[form !== Forms.DEBT ? "assets" : "debts"] += balance;

    if (!flags) return;

    if (flags?.currency) {
      const productCurrency =
        form === Forms.CRYPTO || form === Forms.SHARE || form === Forms.RSU
          ? cashCurrency?.value
          : currentCurrency?.value;

      if (productCurrency !== "ILS") totalBalances["foreignAssets"] += balance;
    }

    if (flags?.liquid && form !== Forms.DEBT) {
      if (product.liquidity) totalBalances["liquidAssets"] += balance;
      else { totalBalances["nonLiquidAssets"] += balance;}
    }

    if (flags?.autoImported && (product.provider === "Masleka" || product.provider === "Vezgo" || product.openfinance_asset_id || product.quiltt_account_id)) {
      totalBalances["autoImported"] += balance;
    }

    if (flags?.bankBalance) {
      if (
        product.form === Forms.BANK &&
        product?.accountType?.value === "balance"
      ) {
        totalBalances["balanceAssets"] += balance;
        totalBalances["balanceAssetNames"]?.push(product?.name);
      }
    }

    if (flags?.form || flags?.allForms) {
      const options: Record<string, string> = flags?.form
        ? {
            [Forms.BANK]: "bankAssets",
            [Forms.SHARE]: "shareAssets",
            [Forms.RSU]: "rsuAssets",
            [Forms.CRYPTO]: "cryptoAssets",
            [Forms.OTHER]: "otherAssets",
          }
        : {
            [Forms.BANK]: "bankAssets",
            [Forms.PENSION]: "pensionAssets",
            [Forms.SHARE]: "shareAssets",
            [Forms.RSU]: "rsuAssets",
            [Forms.CRYPTO]: "cryptoAssets",
            [Forms.DEBT]: "debtAssets",
            [Forms.REALESTATE]: "realestateAssets",
            [Forms.OTHER]: "otherAssets",
          };

      const option = options[getDisplayCategoryForm(product)];
      if (option)
        totalBalances[option as keyof typeof totalBalances] += balance;

      if (flags?.allForms) {
        const lastUpdateForm = options[getDisplayCategoryForm(product)];
        if (lastUpdateForm && totalBalances?.lastUpdate) {
          const lastUpdate = totalBalances?.lastUpdate[lastUpdateForm];
          if (!lastUpdate)
            totalBalances.lastUpdate[lastUpdateForm] = product.updatedAt;
          else {
            const lastUpdatedDate = new Date(lastUpdate);
            const currentUpdatedDate = new Date(product.updatedAt);
            if (lastUpdatedDate < currentUpdatedDate)
              totalBalances.lastUpdate[lastUpdateForm] = product.updatedAt;
          }
        }
      }
    }
  });
  return { balancesInBase, interests, totalBalances };
};
